import React from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';

const MarketingAnalyticsDashboard = () => {
  // Marketing Analytics Data
  const marketingData = {
    totalTraffic: { value: '4.0M', change: '+5.8%', period: 'vs previous period' },
    organicTraffic: { value: '2.9M', change: '-3.5%', period: 'vs previous period' },
    paidTraffic: { value: '1.1M', change: '+38.0%', period: 'vs previous period' },
    adCost: { value: '$259.2K', change: '+7.1%', period: 'vs previous period' }
  };

  // Traffic Sources Distribution Chart
  const trafficSourcesOption = {
    tooltip: {
      trigger: 'item'
    },
    series: [
      {
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: '#fff',
          borderWidth: 2
        },
        label: {
          show: false,
          position: 'center'
        },
        emphasis: {
          label: {
            show: true,
            fontSize: '18',
            fontWeight: 'bold'
          }
        },
        labelLine: {
          show: false
        },
        data: [
          { value: 45, name: 'Google Ads', itemStyle: { color: '#4285F4' } },
          { value: 30, name: 'Facebook/Meta', itemStyle: { color: '#1877F2' } },
          { value: 15, name: 'LinkedIn', itemStyle: { color: '#0077B5' } },
          { value: 10, name: 'Other', itemStyle: { color: '#6B7280' } }
        ]
      }
    ]
  };

  // CTR & Ad Cost Chart
  const ctrCostChartOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: [
      {
        type: 'category',
        data: ['Q3', 'Q4', 'Q1', 'Q2'],
        axisTick: {
          alignWithLabel: true
        },
        axisLabel: {
          color: '#6B7280'
        }
      }
    ],
    yAxis: [
      {
        type: 'value',
        axisLabel: {
          color: '#6B7280'
        }
      }
    ],
    series: [
      {
        name: 'CTR',
        type: 'bar',
        itemStyle: {
          color: '#F59E0B'
        },
        data: [2.1, 2.8, 3.2, 3.5]
      },
      {
        name: 'Ad Cost',
        type: 'bar',
        itemStyle: {
          color: '#EF4444'
        },
        data: [45, 52, 48, 61]
      }
    ]
  };

  // Campaign Performance Chart
  const campaignPerformanceOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: [
      {
        type: 'category',
        data: ['Google Ads', 'Facebook', 'LinkedIn', 'Twitter'],
        axisTick: {
          alignWithLabel: true
        },
        axisLabel: {
          color: '#6B7280'
        }
      }
    ],
    yAxis: [
      {
        type: 'value',
        axisLabel: {
          color: '#6B7280'
        }
      }
    ],
    series: [
      {
        name: 'ROAS',
        type: 'line',
        smooth: true,
        lineStyle: {
          color: '#10B981',
          width: 3
        },
        itemStyle: {
          color: '#10B981'
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(16, 185, 129, 0.3)' },
              { offset: 1, color: 'rgba(16, 185, 129, 0.05)' }
            ]
          }
        },
        data: [4.2, 3.8, 5.1, 2.9]
      }
    ]
  };

  return (
    <div className="bg-gray-900 rounded-2xl p-6 text-white">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold">Models Dashboard</h3>
        </div>
        <div className="text-sm text-gray-400">JAN 2025 TO MAR 2025</div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <motion.div
          className="bg-gray-800 rounded-lg p-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="text-xl font-bold text-white">{marketingData.totalTraffic.value}</div>
          <div className="text-xs text-gray-400">TOTAL TRAFFIC</div>
          <div className="text-xs text-green-400">{marketingData.totalTraffic.change} {marketingData.totalTraffic.period}</div>
        </motion.div>

        <motion.div
          className="bg-gray-800 rounded-lg p-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="text-xl font-bold text-white">{marketingData.organicTraffic.value}</div>
          <div className="text-xs text-gray-400">ORGANIC TRAFFIC</div>
          <div className="text-xs text-red-400">{marketingData.organicTraffic.change} {marketingData.organicTraffic.period}</div>
        </motion.div>

        <motion.div
          className="bg-gray-800 rounded-lg p-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="text-xl font-bold text-white">{marketingData.paidTraffic.value}</div>
          <div className="text-xs text-gray-400">PAID TRAFFIC</div>
          <div className="text-xs text-green-400">{marketingData.paidTraffic.change} {marketingData.paidTraffic.period}</div>
        </motion.div>

        <motion.div
          className="bg-gray-800 rounded-lg p-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="text-xl font-bold text-white">{marketingData.adCost.value}</div>
          <div className="text-xs text-gray-400">AD COST</div>
          <div className="text-xs text-green-400">{marketingData.adCost.change} {marketingData.adCost.period}</div>
        </motion.div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Traffic Sources */}
        <motion.div
          className="bg-gray-800 rounded-lg p-3"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5 }}
        >
          <h4 className="text-sm font-semibold text-gray-300 mb-2">Traffic Sources</h4>
          <div className="h-32">
            <ReactECharts option={trafficSourcesOption} style={{ height: '100%', width: '100%' }} />
          </div>
          <div className="flex justify-center gap-4 mt-2">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="text-xs text-gray-400">Google</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
              <span className="text-xs text-gray-400">Facebook</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-blue-700 rounded-full"></div>
              <span className="text-xs text-gray-400">LinkedIn</span>
            </div>
          </div>
        </motion.div>

        {/* CTR & Ad Cost */}
        <motion.div
          className="bg-gray-800 rounded-lg p-3"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6 }}
        >
          <h4 className="text-sm font-semibold text-gray-300 mb-2">CTR & Ad Cost</h4>
          <div className="h-32">
            <ReactECharts option={ctrCostChartOption} style={{ height: '100%', width: '100%' }} />
          </div>
        </motion.div>
      </div>

      {/* Campaign Performance */}
      <motion.div
        className="mt-4 bg-gray-800 rounded-lg p-3"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <h4 className="text-sm font-semibold text-gray-300 mb-2">Campaign Performance</h4>
        <div className="h-32">
          <ReactECharts option={campaignPerformanceOption} style={{ height: '100%', width: '100%' }} />
        </div>
      </motion.div>

      {/* Bottom Section */}
      <div className="mt-4 flex justify-between items-center">
        <motion.div
          className="bg-gray-800 rounded-lg p-3"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8 }}
        >
          <div className="text-sm font-semibold text-gray-300 mb-1">Top Brand Keywords</div>
          <div className="text-lg font-bold text-white">2.9M</div>
        </motion.div>

        <motion.div
          className="bg-gray-800 rounded-lg p-3"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.9 }}
        >
          <div className="text-sm font-semibold text-gray-300 mb-1">Keyword Performance</div>
          <div className="flex gap-2">
            <div className="text-xs bg-blue-600 px-2 py-1 rounded">Brand</div>
            <div className="text-xs bg-green-600 px-2 py-1 rounded">Generic</div>
            <div className="text-xs bg-purple-600 px-2 py-1 rounded">Long-tail</div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default MarketingAnalyticsDashboard;
